















































import { Component, Vue, Prop, Ref, Watch } from 'vue-property-decorator';
import { FormModel } from 'ant-design-vue';
import {Moment} from 'moment';
import SearchQuotation from '@/components/searchQuotation.vue';
import { pubsub, CLOSE_TAG_VIEW_EVENT } from '@/utils/pubsub';
import { disabledDate, generateUUID } from '@/utils/other';
import TemplateTodoForm, { createNewFormItem, IFormItemInfo } from './templateTodoForm.vue';
import { ITodoTypeOptions, todoTypeTool } from '@/utils/todo';
import { filterSettlementUnits, getAllSettlementUnits } from '@/utils/settlementUnit';
import { deepClone } from '@/utils/deepClone';

type ISettlementUnitInfo = ISettlementUnit.IResponse.ISettlementUnitInfo;
type IModuleTemplateParams = IModule.IRequest.IModuleTemplateParams;
type IModuleTemplateInfo = IModule.IResponse.IModuleTemplateInfo;

interface IForm {
  title: string;
  due: Moment | null;
  quotationId: string;
  manDay: number | null;
  comment: string;
}

const findTypeById = (list: ITodoTypeOptions[], id: string) => {
  const cloneList: ITodoTypeOptions[] = JSON.parse(JSON.stringify(list));
  const idList: string[] = [];
  const recursionByTree = (arr: ITodoTypeOptions[], parentId: string) => {
    arr.forEach((x) => {
      if (x.children) {
        recursionByTree(x.children, `${parentId}${x.value}/`);
      } else {
        idList.push(`${parentId}${x.value}`);
      }
    });
  };
  recursionByTree(cloneList, '');
  const result = idList.find((item) => item.includes(id));
  if (result) {
    return result.split('/');
  } else {
    return [];
  }
};

@Component({
  components: {
    SearchQuotation,
    TemplateTodoForm,
  },
})
export default class CreateTemplateForm extends Vue {
  private submitLoading: boolean = false;
  private todoTypeOptions: ITodoTypeOptions[] = [];
  private settlementUnitOptions: ISettlementUnitInfo[] = [];
  private disabledDate = disabledDate;
  private projectId = '';
  private projectName = '';
  private formList: IFormItemInfo[] = [];
  get formTitle() {
    if (this.isCreate) {
      return this.$t('module.createTemplate');
    }
    return this.$t('module.editTemplate');
  }
  get todoListTitle() {
    if (this.isCreate) {
      return this.$t('module.createTemplateTodo');
    }
    return this.$t('template.todo');
  }
  @Ref() private readonly templateForm!: FormModel;
  @Ref() private readonly searchQuotation!: SearchQuotation;
  get isCreate() {
    return this.template === undefined;
  }
  get rules() {
    return {
      title: [{ required: true, message: () => this.$t('form.required'), trigger: 'blur'}],
      quotationId: [{ required: this.isCreate, message: () => this.$t('form.required'), trigger: 'change'}],
      manDay: [{ required: true, message: () => this.$t('form.required'), trigger: 'blur'}],
    };
  }
  @Prop({required: true}) private template!: IModuleTemplateInfo | undefined;
  @Prop({required: true}) private submit!: (
    updateInfo: IModuleTemplateParams,
  ) => Promise<string>;
  private form: IForm = {
    title: '',
    quotationId: '',
    due: null,
    manDay: null,
    comment: '',
  };
  private created() {
    this.initData();
    this.initTodoTypeOptions();
    getAllSettlementUnits().then(() => {
      this.settlementUnitOptions = filterSettlementUnits('active', true);
    }).catch((err) => err);
  }
  private initData() {
    if (this.template) {
      const {moduleTitle, manDay, comment, projectId, projectName, templateTodoList} = this.template;
      this.form = {
        title: moduleTitle,
        quotationId: '',
        due: null,
        manDay,
        comment: comment || '',
      };
      this.projectId = projectId;
      this.projectName = projectName;
      this.formList = [];
    } else {
      this.form = {
        title: '',
        quotationId: '',
        due: null,
        manDay: null,
        comment: '',
      };
      this.projectId = '';
      this.projectName = '';
      this.formList = [createNewFormItem()];
    }
  }
  private initTodoTypeOptions() {
    todoTypeTool.requestGetTodoTypes().then(() => {
      if (this.template) {
        this.todoTypeOptions = todoTypeTool.cascadedData[this.template.projectId] || [];
        this.formList = this.template.templateTodoList.map((todo) => {
          const {
            typeId, typeName, title, description, manDay: todoManDay,
            settlementConfigurationId, settlementConfigurationName,
          } = todo;
          return createNewFormItem({
            typeId: findTypeById(this.todoTypeOptions, typeId),
            typeName,
            settlementUnitId: settlementConfigurationId,
            settlementUnitName: settlementConfigurationName,
            title,
            description,
            manDay: todoManDay,
          });
        });
      }
    });
  }
  @Watch('projectId')
  private watchPojectId(newValue: string) {
    if (!newValue) {
      this.todoTypeOptions = [];
    } else {
      this.todoTypeOptions = todoTypeTool.cascadedData[newValue] || [];
    }
  }
  private handleSubmit() {
    this.templateForm.validate((valid) => {
      if (!valid) {
        return;
      }
      let validateResult = true;
      const templateTodoList: IModule.IRequest.IModuleTemplateTodo[] = [];
      for (const formItem of this.formList) {
        const formItemRef = (this.$refs['form-' + formItem.formId] as TemplateTodoForm[])[0];
        if (formItemRef.validate()) {
          templateTodoList.push(formItemRef.generateTodoData());
        } else {
          validateResult = false;
        }
      }
      if (validateResult) {
        const {title, manDay, comment} = this.form;
        let id = '';
        if (this.template) {
          id = this.template.id;
        } else {
          id = generateUUID();
        }
        const updateInfo: IModuleTemplateParams = {
          id,
          moduleId: null,
          projectId: this.projectId,
          projectName: this.projectName,
          comment: comment || '',
          templateContent: {
            title,
            active: true,
            manDay: manDay || 0,
            status: 0,
            templateTodoList,
          },
        };
        this.submitModule(updateInfo);
      } else {
        return false;
      }
    });
  }
  private submitModule(updateInfo: IModuleTemplateParams) {
    this.submitLoading = true;
    this.submit(updateInfo).then((res) => {
      this.$message.success(res);
    }).catch((err) => {
      this.$message.warning(err);
    }).finally(() => {
      this.submitLoading = false;
    });
  }
  private updateQuotation(quotation: ISolution.IResponse.ISolutionInfo | null) {
    if (quotation) {
      this.form.quotationId = quotation.id;
      this.form.title = quotation.title;
      this.projectId = quotation.projectId;
      this.projectName = quotation.projectName;
    } else {
      this.form.quotationId = '';
      this.projectId = '';
      this.projectName = '';
    }
  }
  private removeForm(index: number) {
    this.formList.splice(index, 1);
  }
  private addForm() {
    this.formList.push(createNewFormItem());
  }
  private copyForm(form: IFormItemInfo) {
    const data: IFormItemInfo = deepClone(form);
    data.formId = generateUUID();
    const patt = /_copy\[\d+\]/g;
    if (!patt.test(data.title)) {
      data.title = form.title + `_copy[${this.formList.length}]`;
    } else {
      data.title = data.title.replace(patt, `_copy[${this.formList.length}]`);
    }
    this.formList.push(createNewFormItem(data));
  }
  private goListPage() {
    pubsub.publish(CLOSE_TAG_VIEW_EVENT, this.$route.path);
    this.$router.push({
      name: 'Module',
    });
  }
}
